import { useNavigate } from "react-router-dom";
import { ArrowLeft, Target, TrendingUp, Users, Zap, Network, Rocket, ChartBar, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import BackToDemoButton from "../components/BackToDemoButton";

const AboutPage = () => {
  const navigate = useNavigate();

  const stats = [
    { value: "150+", label: "AI Solutions", icon: Zap },
    { value: "50+", label: "Industries", icon: Globe },
    { value: "10K+", label: "Connections Made", icon: Network },
    { value: "95%", label: "Match Accuracy", icon: Target }
  ];

  const forBusinesses = [
    {
      icon: Target,
      title: "Precision Matching",
      description: "Our AI analyzes your unique business needs and matches you with solutions that actually fit your use case, budget, and technical requirements."
    },
    {
      icon: ChartBar,
      title: "Data-Driven Decisions",
      description: "Get ROI estimates, implementation timelines, and real user reviews to make informed decisions about your AI investments."
    },
    {
      icon: Zap,
      title: "Rapid Implementation",
      description: "Connect directly with vetted AI providers and get from discovery to deployment faster than traditional procurement processes."
    }
  ];

  const forStartups = [
    {
      icon: Users,
      title: "Reach Your Ideal Customers",
      description: "Stop wasting budget on broad marketing. We connect you with businesses actively searching for solutions like yours, with buying intent already established."
    },
    {
      icon: TrendingUp,
      title: "Qualified Lead Generation",
      description: "Every connection is pre-qualified through our detailed assessment. You'll speak with prospects who understand their problem and have budget allocated."
    },
    {
      icon: Network,
      title: "Network Effect Growth",
      description: "As more businesses discover solutions through us, your visibility compounds. Early partners benefit from our growing ecosystem of active buyers."
    },
    {
      icon: Rocket,
      title: "Beyond Just Listing",
      description: "We're not a static directory. Our AI actively recommends your solution in assessments, searches, and personalized matches - driving continuous qualified traffic."
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#121212]">
      <BackToDemoButton />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#121212]/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={20} />
            <span className="text-xl sm:text-2xl font-bold">AI Fit Engine</span>
          </button>
          <div className="flex items-center gap-3 sm:gap-6">
            <button
              onClick={() => navigate('/assessment')}
              className="px-3 sm:px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-xs sm:text-sm font-medium transition-colors"
            >
              Assessment
            </button>
            <button
              onClick={() => navigate('/list-product')}
              className="px-4 sm:px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors text-sm"
            >
              List Product
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="pt-24 pb-16 bg-gradient-to-b from-orange-50 to-white dark:from-orange-900/10 dark:to-[#121212]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            We're Not Just a Marketplace.<br />We're Your Growth Partner.
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-8">
            AI Fit Engine is the intelligent matchmaking platform connecting businesses with the perfect AI solutions—and helping innovative AI startups reach customers who are actively looking for them.
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-16 bg-white dark:bg-[#121212]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="w-8 h-8 mx-auto mb-3 text-orange-500" />
                  <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {stat.value}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* For Businesses Section */}
      <div className="py-16 bg-gray-50 dark:bg-[#1E1E1E]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              For Businesses Seeking AI Solutions
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Stop endless research and disappointing demos. Find AI tools that actually solve your problems.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {forBusinesses.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white dark:bg-[#121212] p-8 rounded-xl border border-gray-200 dark:border-gray-700">
                  <Icon className="w-12 h-12 text-orange-500 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* For Startups Section - Main Focus */}
      <div className="py-20 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              For AI Startups Ready to Scale
            </h2>
            <p className="text-xl text-orange-100 max-w-3xl mx-auto">
              Stop cold outreach. Start connecting with businesses who are actively searching for solutions like yours, with budget ready and problems defined.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {forStartups.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white/10 backdrop-blur p-8 rounded-xl border border-white/20">
                  <Icon className="w-12 h-12 text-white mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-orange-50 text-lg leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/list-product')}
              className="bg-white text-orange-600 hover:bg-orange-50 px-12 py-4 text-lg font-bold rounded-xl"
            >
              List Your AI Solution
            </Button>
            <p className="text-orange-100 mt-4">Join 150+ AI companies growing with us</p>
          </div>
        </div>
      </div>

      {/* How It's Different */}
      <div className="py-16 bg-white dark:bg-[#121212]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Why AI Fit Engine Is Different
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Traditional marketplaces are passive directories. We're an active matchmaking engine.
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-orange-50 dark:from-gray-800 dark:to-orange-900/20 p-8 sm:p-12 rounded-2xl">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                  Traditional Directories
                </h3>
                <ul className="space-y-4 text-gray-600 dark:text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 mt-1">✗</span>
                    <span>Businesses browse through hundreds of options</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 mt-1">✗</span>
                    <span>Startups compete on visibility, not fit</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 mt-1">✗</span>
                    <span>No understanding of actual needs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 mt-1">✗</span>
                    <span>Low conversion, wasted time for everyone</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                  AI Fit Engine
                </h3>
                <ul className="space-y-4 text-gray-900 dark:text-gray-100">
                  <li className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <span className="font-medium">AI analyzes needs and recommends 3-5 perfect matches</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <span className="font-medium">Startups get qualified leads with defined budgets</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <span className="font-medium">Deep assessment of industry, size, use case, and goals</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <span className="font-medium">High-intent connections that convert</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-16 bg-gray-50 dark:bg-[#1E1E1E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Ready to Reach Customers Who Are Already Looking for You?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            Join the platform where buyer intent meets seller expertise. Where discovery leads to deployment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/list-product')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 text-lg"
            >
              List Your AI Product
            </Button>
            <Button
              onClick={() => navigate('/startup/login')}
              variant="outline"
              className="border-2 border-gray-900 dark:border-gray-100 px-10 py-4 text-lg"
            >
              Startup Login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;